import React, { createContext, useContext, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X, WhatsappLogo, CaretDown } from "@phosphor-icons/react";
import DemoModal from "@/components/marketing/DemoModal";
import { INDUSTRY_IDS } from "@/lib/industries";

const MarketingCtx = createContext(null);
export const useMarketing = () => useContext(MarketingCtx);

// Strict three-colour palette — Navy / Gold / Green on white neutral.
const NAVY  = "#0B1E3F";
const GOLD  = "#C8A24B";
const GREEN = "#1F6B4A";

// Click-to-chat only — the number itself is never rendered as visible text
// anywhere on the page, it is only ever used inside the wa.me link target.
const WA_NUMBER = "917305967351";
const WA_TEXT = encodeURIComponent("Hi Vaku team, I'd like to know more about your AI agents.");

const WhatsAppWidget = () => (
  <a href={`https://wa.me/${WA_NUMBER}?text=${WA_TEXT}`} target="_blank" rel="noopener noreferrer"
     data-testid="whatsapp-chat-button"
     className="fixed bottom-6 right-6 z-[60] group flex items-center gap-3">
    <span className="hidden sm:block rounded-full bg-white px-4 py-2.5 text-sm font-semibold shadow-lg border opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
          style={{ color: NAVY, borderColor: `${NAVY}15` }}>
      Chat with Vaku AI
    </span>
    <span className="relative flex items-center justify-center w-14 h-14 rounded-full shadow-xl transition-transform group-hover:scale-105 shrink-0"
          style={{ background: GREEN }}>
      <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: GREEN }} />
      <WhatsappLogo size={28} weight="fill" className="relative text-white" />
    </span>
  </a>
);

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/products", label: "Products", testid: "nav-products" },
  { to: "/use-cases", label: "Use Cases", testid: "nav-use-cases" },
  { to: "/partners", label: "Partners", testid: "nav-partners" },
  { to: "/insights", label: "Insights", testid: "nav-insights" },
];

const SOLUTIONS_MENU = {
  function: [
    { to: "/use-cases#close", label: "Close & Controllership" },
    { to: "/use-cases#p2p", label: "Accounts Payable" },
    { to: "/use-cases#o2c", label: "Accounts Receivable" },
    { to: "/use-cases#treasury", label: "Treasury & FP&A" },
    { to: "/use-cases#tax", label: "Tax & Compliance" },
  ],
  industry: [
    { to: "/solutions/banking", label: "Banking & Financial Services" },
    { to: "/solutions/manufacturing", label: "Manufacturing & Distribution" },
    { to: "/solutions/retail", label: "Retail & Consumer" },
    { to: "/solutions/healthcare", label: "Healthcare & Life Sciences" },
    { to: "/solutions/tech", label: "Technology & Professional Services" },
  ],
};

const SolutionsMenu = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  let closeTimer;
  const scheduleClose = () => { closeTimer = setTimeout(() => setOpen(false), 150); };
  const cancelClose = () => clearTimeout(closeTimer);

  return (
    <div className="relative" onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={scheduleClose}>
      <button data-testid="nav-solutions"
        className={`text-sm font-medium transition-colors flex items-center gap-1.5 ${open ? "text-[#C8A24B]" : "text-[#0B1E3F]/75 hover:text-[#C8A24B]"}`}>
        Solutions <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[520px] z-50" data-testid="nav-solutions-menu">
          <div className="rounded-2xl bg-white p-6 grid grid-cols-2 gap-6 shadow-2xl border"
               style={{ borderColor: `${NAVY}15`, boxShadow: "0 24px 60px -16px rgba(11,30,63,0.25)" }}>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: GOLD }}>By Function</div>
              <div className="space-y-2.5">
                {SOLUTIONS_MENU.function.map((it) => (
                  <Link key={it.to} to={it.to} onClick={onNavigate}
                    className="block text-sm font-medium hover:text-[#C8A24B] transition-colors" style={{ color: `${NAVY}CC` }}>
                    {it.label}
                  </Link>
                ))}
                <Link to="/use-cases" onClick={onNavigate} className="block text-xs font-semibold pt-1" style={{ color: GREEN }}>
                  All functions →
                </Link>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: GOLD }}>By Industry</div>
              <div className="space-y-2.5">
                {SOLUTIONS_MENU.industry.map((it) => (
                  <Link key={it.to} to={it.to} onClick={onNavigate}
                    className="block text-sm font-medium hover:text-[#C8A24B] transition-colors" style={{ color: `${NAVY}CC` }}>
                    {it.label}
                  </Link>
                ))}
                <Link to="/solutions" onClick={onNavigate} className="block text-xs font-semibold pt-1" style={{ color: GREEN }}>
                  All industries →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function MarketingLayout({ children }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState("book-a-demo");
  const [demoIndustry, setDemoIndustry] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Pricing CTAs pass ``source="tailored-quote"`` so the sales-inbox email
  // subject reflects the intent. We guard against ``source`` accidentally
  // being a React SyntheticEvent (when handlers do ``onClick={openDemo}``).
  // Industry pages pass ``source="solutions-<industryId>"`` — we pull the
  // id back out here so the demo form can pre-select that industry.
  const openDemo = (source) => {
    const safeSource = typeof source === "string" && source ? source : "book-a-demo";
    setDemoSource(safeSource);
    const maybeIndustryId = safeSource.replace(/^solutions-/, "");
    setDemoIndustry(INDUSTRY_IDS.includes(maybeIndustryId) ? maybeIndustryId : "");
    setDemoOpen(true);
  };

  return (
    <MarketingCtx.Provider value={{ openDemo, demoSource }}>
      <div className="min-h-screen bg-white text-[#0B1E3F] font-body">
        {/* Nav — white surface, navy ink, gold hover, green pulse */}
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b shadow-[0_1px_0_rgba(11,30,63,0.04)]"
                style={{ borderColor: `${NAVY}10` }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
              <img src="/vaku-logo.png" alt="Vaku" className="h-10 w-10 rounded-lg object-cover" />
              <div className="leading-none">
                <div className="font-black text-lg tracking-tight" style={{ color: NAVY }}>Vaku</div>
                <div className="text-[9px] font-mono tracking-[0.18em] uppercase mt-1" style={{ color: GOLD }}>
                  AI for business
                </div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {NAV.slice(0, 2).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${isActive ? "text-[#C8A24B]" : "text-[#0B1E3F]/75 hover:text-[#C8A24B]"}`}>
                  {n.label}
                </NavLink>
              ))}
              <SolutionsMenu />
              {NAV.slice(2).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${isActive ? "text-[#C8A24B]" : "text-[#0B1E3F]/75 hover:text-[#C8A24B]"}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold transition-colors"
                    style={{ color: `${NAVY}CC` }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = `${NAVY}CC`)}>
                Login
              </Link>
              <button onClick={openDemo} data-testid="nav-book-demo-button"
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors shadow-[0_4px_14px_rgba(11,30,63,0.20)]"
                style={{ background: NAVY }}
                onMouseEnter={(e) => (e.currentTarget.style.background = GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.background = NAVY)}>
                Book a Demo
              </button>
            </div>

            <button className="md:hidden" style={{ color: NAVY }}
                    onClick={() => setMobileOpen((o) => !o)} data-testid="nav-mobile-toggle">
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
          {mobileOpen && (
            <div className="md:hidden border-t bg-white px-6 py-4 space-y-3"
                 style={{ borderColor: `${NAVY}10` }}>
              {NAV.slice(0, 2).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                  className="block text-sm font-medium hover:text-[#C8A24B]"
                  style={{ color: `${NAVY}D9` }} data-testid={`m-${n.testid}`}>{n.label}</NavLink>
              ))}
              <div className="pt-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: GOLD }}>Solutions</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-1">
                  {[...SOLUTIONS_MENU.function, ...SOLUTIONS_MENU.industry].map((it) => (
                    <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)}
                      className="block text-xs font-medium hover:text-[#C8A24B]" style={{ color: `${NAVY}B3` }}>
                      {it.label}
                    </Link>
                  ))}
                </div>
              </div>
              {NAV.slice(2).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                  className="block text-sm font-medium hover:text-[#C8A24B]"
                  style={{ color: `${NAVY}D9` }} data-testid={`m-${n.testid}`}>{n.label}</NavLink>
              ))}
              <Link to="/login" className="block text-sm font-semibold" style={{ color: `${NAVY}D9` }} data-testid="m-nav-login">Login</Link>
              <button onClick={() => { setMobileOpen(false); openDemo(); }}
                className="w-full rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                style={{ background: NAVY }} data-testid="m-nav-book-demo">Book a Demo</button>
            </div>
          )}
        </header>

        <main>{children}</main>

        {/* Footer — navy surface, gold accents, sparing green dot */}
        <footer className="text-white" style={{ background: NAVY }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Product</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li><Link to="/products" className="hover:text-white">Products</Link></li>
                  <li><Link to="/solutions" className="hover:text-white">Solutions</Link></li>
                  <li><Link to="/use-cases" className="hover:text-white">Use Cases</Link></li>
                  <li><Link to="/insights" className="hover:text-white">Insights</Link></li>
                  <li><Link to="/partners" className="hover:text-white">Partners</Link></li>
                  <li><Link to="/login" className="hover:text-white">Login</Link></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Connects to</div>
                <ul className="space-y-2 text-sm text-white/80 font-mono">
                  <li>Oracle Fusion</li><li>Oracle EBS</li><li>Microsoft Dynamics 365</li><li>IFS</li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Company</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li><button onClick={openDemo} className="hover:text-white" data-testid="footer-book-demo">Book a Demo</button></li>
                  <li><a href="mailto:Karthikk@vakuai.com" className="hover:text-white">Karthikk@vakuai.com</a></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Security</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    AES-256 encryption
                  </li>
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    Human-in-the-loop
                  </li>
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    Full audit trails
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div className="text-[clamp(3rem,12vw,9rem)] font-black leading-none tracking-tighter" style={{ color: GOLD }}>VĀKU</div>
              <div className="text-xs text-white/40 font-mono">© {new Date().getFullYear()} Vaku · AI for business</div>
            </div>
          </div>
        </footer>

        <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} source={demoSource} industry={demoIndustry} />
        <WhatsAppWidget />
      </div>
    </MarketingCtx.Provider>
  );
}
